'use client';

import { c as _c } from "react/compiler-runtime";
import './social-proof-toast.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';
import type { ToastData } from './social-proof-toast.logic.ts';
import { useSocialProofToasts } from './social-proof-toast.logic.ts';
export interface SocialProofToastProps {
  /** AppSync HTTP endpoint URL */
  appSyncUrl: string;
  /** AppSync API key */
  apiKey: string;
  /** Optional source code filter */
  sourceCode?: string;
  /** Auto-dismiss duration in ms (default: 5000) */
  dismissDuration?: number;
  /** Whether the toast system is enabled (default: true) */
  enabled?: boolean;
}
export interface SocialProofToastViewProps {
  /** Toast data to display */
  toast: ToastData;
  /** Whether the toast is in the dismissing animation state */
  isDismissing?: boolean;
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void;
  /** Callback when the dismiss animation completes */
  onAnimationEnd?: () => void;
}

/** Presentational component — renders the toast UI without any data-fetching logic. */
export const SocialProofToastView = t0 => {
  const $ = _c(30);
  const {
    toast,
    isDismissing: t1,
    onDismiss,
    onAnimationEnd
  } = t0;
  const isDismissing = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["skippy"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  const {
    beneficiaryName,
    beneficiaryImageUrl,
    firstName,
    state
  } = toast;
  const [imgFailed, setImgFailed] = useState(false);
  let showImage;
  let t3;
  if ($[1] !== beneficiaryImageUrl || $[2] !== beneficiaryName || $[3] !== firstName || $[4] !== imgFailed || $[5] !== state || $[6] !== t) {
    const sponsor = firstName != null && firstName.trim().length > 0 ? firstName : t("skippy:socialProofToast.defaultSponsor");
    showImage = beneficiaryImageUrl && !imgFailed;
    t3 = state != null && state.trim().length > 0 ? t("skippy:socialProofToast.messageWithLocation", {
      beneficiaryName,
      sponsor,
      state
    }) : t("skippy:socialProofToast.message", {
      beneficiaryName,
      sponsor
    });
    $[1] = beneficiaryImageUrl;
    $[2] = beneficiaryName;
    $[3] = firstName;
    $[4] = imgFailed;
    $[5] = state;
    $[6] = t;
    $[7] = showImage;
    $[8] = t3;
  } else {
    showImage = $[7];
    t3 = $[8];
  }
  const message = t3;
  const t4 = isDismissing && "social-proof-toast--dismissing";
  let t5;
  if ($[9] !== t4) {
    t5 = ["social-proof-toast", t4].filter(Boolean);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const containerClass = t5.join(" ");
  const t6 = isDismissing ? onAnimationEnd : undefined;
  let t7;
  if ($[11] !== beneficiaryImageUrl || $[12] !== showImage) {
    t7 = showImage ? <img className="social-proof-toast__image" src={loader({
      src: beneficiaryImageUrl,
      width: 80,
      quality: 75
    })} alt="" aria-hidden="true" width={40} height={40} loading="lazy" decoding="async" onError={() => setImgFailed(true)} /> : <div className="social-proof-toast__image social-proof-toast__image--placeholder" aria-hidden="true" />;
    $[11] = beneficiaryImageUrl;
    $[12] = showImage;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== message) {
    t8 = <span className="social-proof-toast__message label-md">{message}</span>;
    $[14] = message;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("skippy:socialProofToast.dismiss");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ArkIcon iconName="x-close" />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== onDismiss || $[20] !== t9) {
    t11 = <button type="button" className="social-proof-toast__close" aria-label={t9} onClick={onDismiss}>{t10}</button>;
    $[19] = onDismiss;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t7 || $[24] !== t8) {
    t12 = <div className="flex items-center gap-comfy-sm py-comfy-md px-comfy-lg">{t7}{t8}{t11}</div>;
    $[22] = t11;
    $[23] = t7;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== containerClass || $[27] !== t12 || $[28] !== t6) {
    t13 = <div role="status" aria-live="polite" className={containerClass} onAnimationEnd={t6}>{t12}</div>;
    $[26] = containerClass;
    $[27] = t12;
    $[28] = t6;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
export const SocialProofToast = t0 => {
  const $ = _c(11);
  const {
    appSyncUrl,
    apiKey,
    sourceCode,
    dismissDuration: t1,
    enabled
  } = t0;
  const dismissDuration = t1 === undefined ? 5000 : t1;
  let t2;
  if ($[0] !== apiKey || $[1] !== appSyncUrl || $[2] !== dismissDuration || $[3] !== enabled || $[4] !== sourceCode) {
    t2 = {
      appSyncUrl,
      apiKey,
      sourceCode,
      dismissDuration,
      enabled
    };
    $[0] = apiKey;
    $[1] = appSyncUrl;
    $[2] = dismissDuration;
    $[3] = enabled;
    $[4] = sourceCode;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    currentToast,
    isDismissing,
    handleDismiss,
    handleAnimationEnd
  } = useSocialProofToasts(t2);
  if (!currentToast) {
    return null;
  }
  let t3;
  if ($[6] !== currentToast || $[7] !== handleAnimationEnd || $[8] !== handleDismiss || $[9] !== isDismissing) {
    t3 = <SocialProofToastView key={currentToast.id} toast={currentToast} isDismissing={isDismissing} onDismiss={handleDismiss} onAnimationEnd={handleAnimationEnd} />;
    $[6] = currentToast;
    $[7] = handleAnimationEnd;
    $[8] = handleDismiss;
    $[9] = isDismissing;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};